/**
 * 编排器：组件生命周期 + 事件分发 + 冲突评估 + 导出 + dirty 判定。
 *
 * 对外接口（被 index.js 包装成 publicApi）：
 *  - addComponent / removeComponent / restoreComponents
 *  - setSelectedId / getComponents
 *  - setComponentMagnifier
 *  - switchMode
 *  - hasConflict / getConflictIds
 *  - isDirty
 *  - exportLayerDataPatch
 *  - destroy
 *  - update（强制重绘）
 *
 * 立牌刀线分支（spec §4.8）：addComponent / removeComponent / touchend 位置变化 /
 * 内插口 touchend 位置变化后，调用 refreshOutlineAfterLiPaiChange（多立牌时取最后一个
 * 立牌的 slotRect；后端 API 单 slotRect 限制）；删除最后一个立牌时走
 * restoreOriginalOutline（imageBorderParse 不传 slotRect 拿原始 outline）。
 */

import { Toast, api, showLoading, hideLoading } from '@/utils/common.js';

import { DRAW_TYPE, DEFAULT_CONFIG, modeEdit, modeDraw } from './constants.js';
import { getComponentType } from './typeRegistry.js';
import { validateAddComponent } from './validateComponentConfig.js';
import { evaluateAll } from './collision.js';
import { createOutlineRenderer } from './outlineRenderer.js';
import { getCanvasPoint, updateSharedOutlinePoints } from './core.js';

let _idSeed = 0;
function genId(type) {
  _idSeed++;
  return 'cmp-' + type + '-' + _idSeed + '-' + Date.now().toString(36);
}

/**
 * 创建编排器。
 *
 * @param {Object} sharedState
 * @param {Object} serviceConfig
 * @param {Object} [serviceConfig.maxCount] - 覆盖 DEFAULT_CONFIG.maxCount（只对 perforation / baseHole 生效）
 * @param {number} [serviceConfig.componentMinGapMm] - 默认 0
 * @param {boolean} [serviceConfig.enableMagnifierGlobal] - 默认 true
 * @param {boolean} [serviceConfig.disableTouchEvent] - 默认 false
 * @returns {Object} orchestrator
 */
export function createOrchestrator(sharedState, serviceConfig) {
  const cfg = serviceConfig || {};

  // 合并 maxCount：serviceConfig.maxCount[type] → DEFAULT_CONFIG.maxCount[type]
  const maxCount = {};
  [
    DRAW_TYPE.KEY_HOLE,
    DRAW_TYPE.LI_PAI,
    DRAW_TYPE.PERFORATION,
    DRAW_TYPE.BASE_HOLE
  ].forEach(function (t) {
    maxCount[t] =
      cfg.maxCount && cfg.maxCount[t] != null
        ? cfg.maxCount[t]
        : DEFAULT_CONFIG.maxCount[t];
  });

  const componentMinGapMm =
    cfg.componentMinGapMm != null
      ? cfg.componentMinGapMm
      : DEFAULT_CONFIG.componentMinGapMm;
  const componentToOutlineMinGapMm =
    cfg.componentToOutlineMinGapMm != null
      ? cfg.componentToOutlineMinGapMm
      : DEFAULT_CONFIG.componentToOutlineMinGapMm;
  const enableMagnifierGlobal =
    cfg.enableMagnifierGlobal != null
      ? cfg.enableMagnifierGlobal
      : DEFAULT_CONFIG.enableMagnifierGlobalDefault;
  const disableTouchEvent = !!cfg.disableTouchEvent;

  const state = {
    components: [],
    selectedId: null,
    /** 每种 type 最近选中的组件 id，用于新增时确定右偏移参考实例 */
    lastInteractedId: {
      [DRAW_TYPE.KEY_HOLE]: null,
      [DRAW_TYPE.LI_PAI]: null,
      [DRAW_TYPE.PERFORATION]: null,
      [DRAW_TYPE.BASE_HOLE]: null
    },
    conflictIds: [],
    hasConflictCache: false,
    dirtyBaseline: '',
    draggingInstance: null,
    touchStartPoint: null
  };

  const renderer = createOutlineRenderer(sharedState);

  /** mm→px 间隙 */
  function getMinGapPx() {
    return componentMinGapMm * sharedState.mmToPixelRatio;
  }

  // 穿孔/底座孔与刀线间距（px），写入 sharedState 供各 type clampSelf 使用
  sharedState.componentToOutlineGapPx =
    componentToOutlineMinGapMm * sharedState.mmToPixelRatio;

  /**
   * 获取同类型新增时的右偏移参考实例。
   * 优先取该类型最近选中的实例，找不到则回退最右侧。
   * @param {string} type
   * @returns {Object|null}
   */
  function getReferenceInstance(type) {
    var sameType = state.components.filter(function (c) {
      return c.type === type;
    });
    if (!sameType.length) return null;
    // 优先取该类型最近选中的实例
    var lastId = state.lastInteractedId[type];
    if (lastId) {
      for (var i = 0; i < sameType.length; i++) {
        if (sameType[i].id === lastId) {
          return sameType[i];
        }
      }
    }
    // 回退：最右侧
    var rightMost = sameType[0];
    for (var j = 1; j < sameType.length; j++) {
      if (sameType[j].centerPoint.x > rightMost.centerPoint.x) {
        rightMost = sameType[j];
      }
    }
    return rightMost;
  }

  /**
   * 穿孔↔底座孔交叉偏移量：无同类型但另一类型存在时，向下偏移避免重叠。
   * @param {string} type
   * @returns {number}
   */
  function getCrossTypeOffsetY(type) {
    var sameType = state.components.filter(function (c) {
      return c.type === type;
    });
    if (sameType.length > 0) return 0;
    var hasOther = false;
    if (type === DRAW_TYPE.PERFORATION) {
      hasOther = state.components.some(function (c) {
        return c.type === DRAW_TYPE.BASE_HOLE;
      });
    } else if (type === DRAW_TYPE.BASE_HOLE) {
      hasOther = state.components.some(function (c) {
        return c.type === DRAW_TYPE.PERFORATION;
      });
    }
    return hasOther ? DEFAULT_CONFIG.crossTypeOffsetPx : 0;
  }

  /**
   * 触发完整重绘（异步，渲染器内部已做 destroyed guard）。
   * @returns {Promise<void>}
   */
  /**
   * 取 draw 模式下的孔位区域，用于刀线过滤 + 底图抠除 + mask 导出。
   * 返回统一 { kind, type, cx, cy, ... } 结构，调用方按 kind 区分处理方式。
   * @returns {Array<Object>}
   */
  function getHoleRegions() {
    if (sharedState.mode !== modeDraw) return [];
    var regions = [];
    state.components.forEach(function (c) {
      if (c.type === DRAW_TYPE.KEY_HOLE) {
        regions.push({
          kind: 'circle',
          type: DRAW_TYPE.KEY_HOLE,
          cx: c.centerPoint.x,
          cy: c.centerPoint.y,
          radius: c.outRadius,
          inRadius: c.inRadius
        });
      } else if (c.type === DRAW_TYPE.PERFORATION) {
        regions.push({
          kind: 'circle',
          type: DRAW_TYPE.PERFORATION,
          cx: c.centerPoint.x,
          cy: c.centerPoint.y,
          radius: c.radius
        });
      } else if (c.type === DRAW_TYPE.BASE_HOLE) {
        regions.push({
          kind: 'rotatedRect',
          type: DRAW_TYPE.BASE_HOLE,
          cx: c.centerPoint.x,
          cy: c.centerPoint.y,
          w: c.width,
          h: c.height,
          rotationRad: c.rotationRad,
          cornerRadiusPercent: DEFAULT_CONFIG.baseHoleCornerRadiusPercent
        });
      }
    });
    return regions;
  }

  async function update() {
    if (sharedState.destroyed) return;
    await renderer.render({
      components: state.components,
      selectedId: state.selectedId,
      conflictIds: state.conflictIds,
      enableMagnifierGlobal,
      holeRegions: getHoleRegions()
    });
  }

  /**
   * 取当前所有立牌实例。
   * @returns {Array<Object>}
   */
  function getLiPaiInstances() {
    return state.components.filter((c) => c.type === DRAW_TYPE.LI_PAI);
  }

  /**
   * 立牌新增 / 拖动 / 切贴边后刷新共享刀线。
   *
   * 当前后端 imageBorderParse 接受 **单个** slotRect。多立牌时只发最后一个立牌的
   * slotRect（视觉上其它立牌仍叠在原刀线上）。后续若后端支持多 slotRect，再扩
   * 这里聚合数组传入即可，types/liPai.js 不需要改。
   *
   * @returns {Promise<void>}
   */
  async function refreshOutlineAfterLiPaiChange() {
    const liPais = getLiPaiInstances();
    if (!liPais.length) return;
    const params = sharedState.imageBorderParseParams;
    if (!params) {
      Toast('缺少 imageBorderParseParams，无法刷新刀线');
      return;
    }
    const last = liPais[liPais.length - 1];
    const strategy = getComponentType(DRAW_TYPE.LI_PAI);
    if (!strategy || typeof strategy.getSlotRectForApi !== 'function') return;
    const slotRect = strategy.getSlotRectForApi(last, sharedState);
    try {
      showLoading('正在处理中...', true);
      const res = await api.imageBorderParse({
        ...params,
        slotRect
      });
      window._debugLog({
        title: '自定义组件imageBorderParse接口返回mask:',
        mask: 'data:image/png;base64,' + res.mask
      });
      hideLoading();
      if (res && res.outline) {
        updateSharedOutlinePoints(sharedState, res.outline);
        // 立牌槽口会改变 mask 形状，同步更新供 exportImageBySrc 使用
        if (res.mask) {
          sharedState.mask = 'data:image/png;base64,' + res.mask;
        }
        // 刀线变了：非 baseHole 可 clamp；baseHole 不自动挪位（R2 只标冲突，spec §4.8）
        state.components.forEach((inst) => {
          if (inst.type === DRAW_TYPE.BASE_HOLE) return;
          const s = getComponentType(inst.type);
          if (s && typeof s.clampSelf === 'function') {
            s.clampSelf(inst, sharedState);
          }
        });
        refreshConflict({ skipClampTypes: [DRAW_TYPE.BASE_HOLE] });
      }
    } catch (e) {
      hideLoading();
      console.error('[serviceComponentHole] refreshOutlineAfterLiPaiChange', e);
    }
  }

  /**
   * 删除最后一个立牌时恢复"无槽口"的原始刀线（imageBorderParse 不传 slotRect，
   * 参考 edit.vue#4019 钥匙扣分支调用方式）。
   *
   * @returns {Promise<void>}
   */
  async function restoreOriginalOutline() {
    const params = sharedState.imageBorderParseParams;
    if (!params) return;
    try {
      showLoading('正在处理中...', true);
      const res = await api.imageBorderParse({ ...params });
      hideLoading();
      if (res && res.outline) {
        updateSharedOutlinePoints(sharedState, res.outline);
        // 删除最后立牌后恢复原始 mask（无槽口）
        if (res.mask) {
          sharedState.mask = 'data:image/png;base64,' + res.mask;
        }
        state.components.forEach((inst) => {
          if (inst.type === DRAW_TYPE.BASE_HOLE) return;
          const s = getComponentType(inst.type);
          if (s && typeof s.clampSelf === 'function') {
            s.clampSelf(inst, sharedState);
          }
        });
        refreshConflict({ skipClampTypes: [DRAW_TYPE.BASE_HOLE] });
      }
    } catch (e) {
      hideLoading();
      console.error('[serviceComponentHole] restoreOriginalOutline', e);
    }
  }

  /**
   * 立牌 slot 快照（用于 touchend 比对是否需要刷新刀线）。
   * @param {Object} instance
   * @returns {string}
   */
  function snapshotLiPaiSlot(instance) {
    if (!instance || instance.type !== DRAW_TYPE.LI_PAI) return '';
    const s = instance.slotRect;
    if (!s) return '';
    return [
      Math.round(s.x * 100) / 100,
      Math.round(s.y * 100) / 100,
      Math.round(s.w * 100) / 100,
      Math.round(s.h * 100) / 100
    ].join('|');
  }

  /**
   * 重新评估冲突，结果写入 state（不重绘）。
   * @param {Object} [options]
   * @param {string[]} [options.skipClampTypes] - 见 collision.evaluateAll
   * @returns {void}
   */
  function refreshConflict(options) {
    const r = evaluateAll({
      components: state.components,
      sharedState,
      minGapPx: getMinGapPx(),
      skipClampTypes: options && options.skipClampTypes
    });
    state.conflictIds = r.conflictIds;
    state.hasConflictCache = r.hasConflict;
  }

  /**
   * 序列化 components 作为 dirty baseline 对比基线。
   * 只保留对位置/几何敏感的字段；isDragging 等运行态字段被忽略。
   * @param {Array<Object>} components
   * @returns {string}
   */
  function serializeForDirty(components) {
    return JSON.stringify(
      components.map((c) => {
        const strategy = getComponentType(c.type);
        if (strategy && typeof strategy.serializeForDirty === 'function') {
          return { type: c.type, snap: strategy.serializeForDirty(c) };
        }
        return { type: c.type, snap: null };
      })
    );
  }

  /**
   * 把当前 components 设为 dirty 比较基线。init 完成时调一次。
   * @returns {void}
   */
  function setDirtyBaseline() {
    state.dirtyBaseline = serializeForDirty(state.components);
  }

  /**
   * @returns {boolean}
   */
  function isDirty() {
    return serializeForDirty(state.components) !== state.dirtyBaseline;
  }

  /**
   * 添加组件。
   * @param {string} type
   * @param {Object} options
   * @returns {Promise<{id:string}>}
   */
  async function addComponent(type, options) {
    const opts = options || {};
    const err = validateAddComponent(type, opts);
    if (err) {
      Toast(err);
      throw new Error(err);
    }
    const strategy = getComponentType(type);
    if (!strategy) {
      const m = '组件类型未注册：' + type;
      Toast(m);
      throw new Error(m);
    }

    const sameTypeCount = state.components.filter((c) => c.type === type)
      .length;
    if (sameTypeCount >= (maxCount[type] || 0)) {
      const m = '已达上限';
      Toast(m);
      throw new Error(m);
    }

    // 立牌：imageBorderParseParams 优先用 options，其次 fallback 到 sharedState
    if (type === DRAW_TYPE.LI_PAI) {
      const p =
        opts.imageBorderParseParams || sharedState.imageBorderParseParams;
      if (!p || !p.fileName || !p.paperWidth || !p.paperHeight) {
        const m =
          '立牌添加缺少 imageBorderParseParams（fileName/paperWidth/paperHeight）';
        Toast(m);
        throw new Error(m);
      }
      // options 传入 → 写入 sharedState 供后续 refresh 复用
      if (opts.imageBorderParseParams) {
        sharedState.imageBorderParseParams = opts.imageBorderParseParams;
      }
    }

    const instance = {
      id: genId(type),
      type,
      sizePresetId: opts.sizePresetId || '',
      geometryConfig: JSON.parse(JSON.stringify(opts.geometryConfig)),
      enableMagnifier:
        opts.enableMagnifier != null ? !!opts.enableMagnifier : true,
      isDragging: false
    };

    strategy.init(instance, sharedState, {
      placement: opts.placement,
      sameTypeInstances: state.components.filter(function (c) {
        return c.type === type;
      }),
      referenceInstance: getReferenceInstance(type),
      minGapPx: getMinGapPx(),
      crossTypeOffsetY: getCrossTypeOffsetY(type)
    });

    state.components.push(instance);

    if (type === DRAW_TYPE.LI_PAI) {
      await refreshOutlineAfterLiPaiChange();
    }

    if (typeof strategy.clampSelf === 'function') {
      strategy.clampSelf(instance, sharedState);
    }
    refreshConflict();

    if (opts.selectAfterAdd !== false) {
      state.selectedId = instance.id;
      // 新增时同步更新最近选中记录
      state.lastInteractedId[type] = instance.id;
    }

    await update();
    return { id: instance.id };
  }

  /**
   * 删除组件。
   * @param {string} id
   * @returns {Promise<void>}
   */
  async function removeComponent(id) {
    const idx = state.components.findIndex((c) => c.id === id);
    if (idx === -1) return;
    const removed = state.components[idx];
    state.components.splice(idx, 1);

    if (state.selectedId === id) {
      state.selectedId = null;
    }

    if (removed.type === DRAW_TYPE.LI_PAI) {
      if (getLiPaiInstances().length > 0) {
        await refreshOutlineAfterLiPaiChange();
      } else {
        await restoreOriginalOutline();
      }
    }

    refreshConflict();
    await update();
  }

  /**
   * 批量还原（二次进入页面）。
   * @param {Array<Object>} list - [{ type, geometryConfig, placement, ... }]
   * @returns {Promise<void>}
   */
  async function restoreComponents(list) {
    if (!list || !list.length) return;
    for (const item of list) {
      try {
        await addComponent(item.type, {
          geometryConfig: item.geometryConfig,
          placement: item.placement,
          sizePresetId: item.sizePresetId,
          enableMagnifier: item.enableMagnifier,
          imageBorderParseParams: item.imageBorderParseParams,
          selectAfterAdd: false
        });
      } catch (e) {
        console.warn('[serviceComponentHole] restore 跳过一项', item, e);
      }
    }
    state.selectedId = null;
    // 还原完成后再设 baseline，避免还原过程被识别为 dirty
    setDirtyBaseline();
    await update();
  }

  /**
   * 设置选中。
   * @param {string|null} id
   * @returns {Promise<void>}
   */
  async function setSelectedId(id) {
    state.selectedId = id;
    // 按类型记录最近选中的组件，供 addComponent 右偏移参考
    var comp = id
      ? state.components.find(function (c) {
          return c.id === id;
        })
      : null;
    if (comp) {
      state.lastInteractedId[comp.type] = id;
    }
    await update();
  }

  /**
   * 当前所有组件（浅 clone，外部不要直接改）。
   * @returns {Array<Object>}
   */
  function getComponents() {
    return state.components.map((c) => ({ ...c }));
  }

  /**
   * 单组件放大镜开关。
   * @param {string} id
   * @param {boolean} enabled
   * @returns {Promise<void>}
   */
  async function setComponentMagnifier(id, enabled) {
    const c = state.components.find((x) => x.id === id);
    if (!c) return;
    c.enableMagnifier = !!enabled;
    await update();
  }

  /**
   * 切换 edit / draw 模式。
   * @param {string} mode
   * @returns {Promise<void>}
   */
  async function switchMode(mode) {
    if (mode !== modeEdit && mode !== modeDraw) return;
    if (mode === sharedState.mode) return;
    sharedState.mode = mode;
    await update();
  }

  /** @returns {boolean} */
  function hasConflict() {
    return state.hasConflictCache;
  }
  /** @returns {string[]} */
  function getConflictIds() {
    return [...state.conflictIds];
  }

  /**
   * 聚合各 type 的 toExport → layerData *List 补丁对象。
   * @returns {Object}
   */
  function exportLayerDataPatch() {
    const patch = {};
    for (const inst of state.components) {
      const strategy = getComponentType(inst.type);
      if (!strategy || typeof strategy.toExport !== 'function') continue;
      const piece = strategy.toExport(inst, sharedState);
      if (!piece) continue;
      // piece 形如 { keyPointList: [item], originCenterPointList: [item] }
      Object.keys(piece).forEach((key) => {
        if (!patch[key]) patch[key] = [];
        if (Array.isArray(piece[key])) {
          patch[key] = patch[key].concat(piece[key]);
        } else {
          patch[key].push(piece[key]);
        }
      });
    }
    return patch;
  }

  // ---------------- 事件分发 ----------------

  /**
   * 计算命中候选与点击点的距离，用于多个热区重叠时排序。
   * @param {Object} instance - 组件实例
   * @param {{x:number,y:number}} point - canvas 坐标点
   * @returns {number}
   */
  function getHitCandidateDistance(instance, point) {
    var center = instance.centerPoint || instance.slotCenter || null;
    // 立牌等无中心点组件，使用外容器中心作为距离兜底。
    if (!center && instance.containerRect) {
      center = {
        x: instance.containerRect.x + instance.containerRect.w / 2,
        y: instance.containerRect.y + instance.containerRect.h / 2
      };
    }
    // 无法取得中心时回退 0，避免兼容旧策略时报错。
    if (!center) return 0;
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * 命中检测：优先 icon 命中（暂未实现 icon 的 type 跳过），再组件本体。
   * @param {{x:number,y:number}} point
   * @returns {Object|null} hit { instance, target:'icon'|'body' }
   */
  function hitTest(point) {
    // 选中组件的 icon 命中优先（阶段 1 没有 icon，但保留分发路径）
    if (state.selectedId) {
      const selected = state.components.find((c) => c.id === state.selectedId);
      if (selected) {
        const strategy = getComponentType(selected.type);
        if (
          strategy &&
          typeof strategy.hitTestActionIcon === 'function' &&
          strategy.hitTestActionIcon(selected, point, sharedState)
        ) {
          return { instance: selected, target: 'icon' };
        }
      }
    }
    const candidates = [];
    // 组件本体：收集候选后排序，避免小组件扩展热区重叠时误选。
    for (let i = state.components.length - 1; i >= 0; i--) {
      const inst = state.components[i];
      const strategy = getComponentType(inst.type);
      // 未注册策略时跳过该组件，避免影响其它组件命中。
      if (!strategy) continue;
      // 新策略返回真实命中/热区命中信息，供候选排序使用。
      if (typeof strategy.getHitTestInfo === 'function') {
        const info = strategy.getHitTestInfo(inst, point, sharedState);
        // 未命中新热区时，不加入候选列表。
        if (info && info.hit) {
          candidates.push({
            instance: inst,
            isRealHit: !!info.isRealHit,
            distance:
              typeof info.distance === 'number' && isFinite(info.distance)
                ? info.distance
                : 0,
            zIndex: i
          });
        }
      } else if (typeof strategy.hitTest === 'function') {
        // 旧策略保持原命中语义，命中后按真实命中参与排序。
        if (strategy.hitTest(inst, point, sharedState)) {
          candidates.push({
            instance: inst,
            isRealHit: true,
            distance: getHitCandidateDistance(inst, point),
            zIndex: i
          });
        }
      }
    }
    // 存在多个候选时，按真实命中、距离、绘制层级选择最符合意图的组件。
    if (candidates.length) {
      candidates.sort(function (a, b) {
        // 真实几何命中优先于扩展热区命中。
        if (a.isRealHit !== b.isRealHit) {
          return a.isRealHit ? -1 : 1;
        }
        const tie = DEFAULT_CONFIG.componentHitTieDistancePx;
        // 两个候选距离差明显时，选择离点击点更近的组件。
        if (Math.abs(a.distance - b.distance) > tie) {
          return a.distance - b.distance;
        }
        return b.zIndex - a.zIndex;
      });
      return { instance: candidates[0].instance, target: 'body' };
    }
    return null;
  }

  /** canvas 侧选中变化回调（仅 handleTouchStart 触发，避免与 setSelectedId 循环） */
  var _onSelectedChange = null;

  /**
   * 注册 canvas 选中变化回调。
   * @param {Function|null} fn - 回调函数，参数为 componentId
   */
  function setOnSelectedChange(fn) {
    _onSelectedChange = fn;
  }

  let rafScheduled = false;
  /**
   * 拖动中用 rAF 合并多次 touchmove，避免每帧都跑 evaluateAll + render。
   * TODO: 性能优化 - 若仍掉帧，改为「动的组件 vs 其它静态」增量评估
   */
  function scheduleUpdate() {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(() => {
      rafScheduled = false;
      refreshConflict();
      update();
    });
  }

  function handleTouchStart(e) {
    if (sharedState.mode !== modeEdit) return;
    const p = getCanvasPoint(e, sharedState);
    state.touchStartPoint = p;
    const hit = hitTest(p);
    if (!hit) {
      // 点击空白：保留当前 selectedId（spec §4.2）
      return;
    }
    state.selectedId = hit.instance.id;
    if (_onSelectedChange) {
      _onSelectedChange(hit.instance.id);
    }
    // 立牌：start 时记 slot 快照，end 时比对判断是否刷刀线
    if (hit.instance.type === DRAW_TYPE.LI_PAI) {
      hit.instance._slotSnapshotBefore = snapshotLiPaiSlot(hit.instance);
    }
    if (hit.target === 'icon') {
      const strategy = getComponentType(hit.instance.type);
      if (strategy && typeof strategy.onRotateIconStart === 'function') {
        strategy.onRotateIconStart(hit.instance, p, sharedState);
      }
      state.draggingInstance = hit.instance;
      hit.instance.isDragging = true;
      hit.instance._dragTarget = 'icon';
      update();
      return;
    }
    // body 拖动
    state.draggingInstance = hit.instance;
    hit.instance.isDragging = true;
    hit.instance._dragTarget = 'body';
    const strategy = getComponentType(hit.instance.type);
    if (strategy && typeof strategy.onDragStart === 'function') {
      strategy.onDragStart(hit.instance, p, sharedState);
    }
    update();
  }

  function handleTouchMove(e) {
    if (sharedState.mode !== modeEdit) return;
    if (!state.draggingInstance) return;
    // touchmove 期间阻止页面滚动（与旧 changeKeyHole.vue 行为一致）
    if (e.cancelable) e.preventDefault();
    const p = getCanvasPoint(e, sharedState);
    const inst = state.draggingInstance;
    const strategy = getComponentType(inst.type);
    if (!strategy) return;
    if (inst._dragTarget === 'icon') {
      if (typeof strategy.onRotateIconMove === 'function') {
        strategy.onRotateIconMove(inst, p, sharedState);
      }
    } else {
      if (typeof strategy.onDragMove === 'function') {
        strategy.onDragMove(inst, p, sharedState);
      }
    }
    scheduleUpdate();
  }

  async function handleTouchEnd(/* e */) {
    if (!state.draggingInstance) return;
    const inst = state.draggingInstance;
    const wasIcon = inst._dragTarget === 'icon';
    inst.isDragging = false;
    inst._dragTarget = null;
    state.draggingInstance = null;

    const strategy = getComponentType(inst.type);
    if (strategy) {
      if (wasIcon) {
        if (typeof strategy.onRotateIconEnd === 'function') {
          strategy.onRotateIconEnd(inst, sharedState);
        }
      } else {
        if (typeof strategy.onDragEnd === 'function') {
          strategy.onDragEnd(inst, sharedState);
        }
      }
      if (typeof strategy.clampSelf === 'function') {
        strategy.clampSelf(inst, sharedState);
      }
    }

    if (inst.type === DRAW_TYPE.LI_PAI) {
      const after = snapshotLiPaiSlot(inst);
      const before = inst._slotSnapshotBefore || '';
      inst._slotSnapshotBefore = null;
      if (after && before !== after) {
        await refreshOutlineAfterLiPaiChange();
      }
    }
    refreshConflict();
    update();
  }

  /**
   * 绑定 touch / mouse 事件。
   * @returns {void}
   */
  function bindEvents() {
    if (disableTouchEvent) return;
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) ||
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0;
    const pairs = isMobile
      ? [
          ['touchstart', handleTouchStart],
          ['touchmove', handleTouchMove],
          ['touchend', handleTouchEnd],
          ['touchcancel', handleTouchEnd]
        ]
      : [
          ['mousedown', handleTouchStart],
          ['mousemove', handleTouchMove],
          ['mouseup', handleTouchEnd],
          ['mouseleave', handleTouchEnd]
        ];
    pairs.forEach(([type, handler]) => {
      sharedState.canvas.addEventListener(type, handler);
      sharedState.boundEvents.push({ type, handler });
    });
  }

  /**
   * 某 type 的同页数量上限（供页面 disabled / Toast 判断）。
   * @param {string} type
   * @returns {number}
   */
  function getMaxCount(type) {
    return maxCount[type] != null ? maxCount[type] : 0;
  }

  /** @returns {void} */
  function destroy() {
    if (sharedState.destroyed) return;
    sharedState.destroyed = true;
    sharedState.boundEvents.forEach((item) => {
      sharedState.canvas.removeEventListener(item.type, item.handler);
    });
    sharedState.boundEvents = [];
  }

  return {
    addComponent,
    removeComponent,
    restoreComponents,
    setSelectedId,
    getComponents,
    getMaxCount,
    setComponentMagnifier,
    switchMode,
    hasConflict,
    getConflictIds,
    isDirty,
    exportLayerDataPatch,
    update,
    bindEvents,
    destroy,
    getHoleRegions,
    setDirtyBaseline,
    setOnSelectedChange,
    _state: state // 供 index.js 内部使用，不导出到 publicApi
  };
}
